import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTheme } from '@/contexts/ThemeContext';
import PlayerMatchingModal from '@/components/shared/PlayerMatchingModal';
import { getApiUrl } from '@/config/api';
import { CheckCircle2 } from '@/utils/icons';

interface OrgStatus {
    hawkin_connected: boolean;
    hawkin_connected_by: string | null;
}

const ConnectHawkin: React.FC = () => {
    const navigate = useNavigate();
    const { isDark } = useTheme();
    const [apiKey, setApiKey] = useState('');
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');
    const [showMatching, setShowMatching] = useState(false);
    const [userInfo, setUserInfo] = useState<{ username: string; role?: string; full_name?: string; first_name?: string } | null>(null);
    const [orgStatus, setOrgStatus] = useState<OrgStatus | null>(null);
    const [checkingOrg, setCheckingOrg] = useState(true);

    useEffect(() => {
        const access = localStorage.getItem('access');
        if (!access) {
            navigate('/login');
            return;
        }
        const userInfoString = localStorage.getItem('user_info');
        if (userInfoString) {
            try {
                setUserInfo(JSON.parse(userInfoString));
            } catch {
                // ignore
            }
        }

        // Check if a teammate already connected Hawkin for this org
        fetch(getApiUrl('/api/wearables/org-status/'), {
            headers: { 'Authorization': `Bearer ${access}` },
        })
            .then((r) => r.json())
            .then((data) => setOrgStatus(data))
            .catch(() => {})
            .finally(() => setCheckingOrg(false));
    }, [navigate]);

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!apiKey.trim()) {
            setError('Please enter your Hawkin API key.');
            return;
        }
        const access = localStorage.getItem('access');
        if (!access) {
            navigate('/login');
            return;
        }
        setLoading(true);
        setError('');
        setSuccess('');
        try {
            const response = await fetch(getApiUrl('/api/hawkin/connect/'), {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${access}`,
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({ api_key: apiKey.trim() }),
            });
            const data = await response.json().catch(() => ({}));
            if (response.ok) {
                setApiKey('');
                if ((data.athletes || 0) > 0) {
                    setShowMatching(true);
                } else {
                    setSuccess('Hawkin connected! No athletes found yet — they will appear after data syncs.');
                }
            } else {
                setError(data.error || data.details || `Request failed (${response.status})`);
            }
        } catch (err: any) {
            setError(err.message || 'Failed to connect. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    const handleJoinOrg = async () => {
        const access = localStorage.getItem('access');
        if (!access) {
            navigate('/login');
            return;
        }
        setLoading(true);
        setError('');
        setSuccess('');
        try {
            const response = await fetch(getApiUrl('/api/hawkin/join/'), {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${access}`,
                    'Content-Type': 'application/json',
                },
            });
            const data = await response.json().catch(() => ({}));
            if (response.ok) {
                if ((data.athletes || 0) > 0) {
                    setShowMatching(true);
                } else {
                    setSuccess('Hawkin connected! No athletes found yet — they will appear after data syncs.');
                }
            } else {
                setError(data.error || data.details || `Request failed (${response.status})`);
            }
        } catch (err: any) {
            setError(err.message || 'Failed to join. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    const orgAlreadyConnected = orgStatus?.hawkin_connected && !checkingOrg;

    return (
        <div className="cgm-bg-primary min-h-screen flex flex-col items-center justify-center py-8 px-4 sm:px-6 lg:px-8 transition-colors duration-300">

            <div className="max-w-md w-full space-y-6">
                <div className="text-center">
                    <h2 className="cgm-text-primary text-3xl font-light tracking-tight mb-2">
                        Connect Hawkin Dynamics
                    </h2>
                    <p className="cgm-text-secondary text-sm">
                        {orgAlreadyConnected
                            ? 'Hawkin is already connected for your organization. Select your players to get started.'
                            : 'Enter your Hawkin API key to link your force plate data.'}
                    </p>
                </div>

                {error && (
                    <div className="cgm-alert-error px-4 py-3 text-sm">
                        {error}
                    </div>
                )}
                {success && (
                    <div className="cgm-alert-success px-4 py-3 text-sm flex items-center gap-2">
                        <CheckCircle2 className="w-5 h-5 shrink-0" />
                        {success}
                    </div>
                )}

                {checkingOrg ? (
                    <div className="cgm-card p-6 text-center cgm-text-secondary text-sm">
                        Checking organization status…
                    </div>
                ) : orgAlreadyConnected ? (
                    <div className="cgm-card p-6 space-y-4">
                        <div className="flex items-center gap-3 text-sm cgm-text-primary">
                            <CheckCircle2 className="w-5 h-5 text-emerald-500 shrink-0" />
                            <span>
                                {orgStatus.hawkin_connected_by
                                    ? `${orgStatus.hawkin_connected_by} already connected Hawkin for your organization.`
                                    : 'A teammate already connected Hawkin for your organization.'}
                            </span>
                        </div>
                        <button
                            onClick={handleJoinOrg}
                            disabled={loading}
                            className={`cgm-button w-full px-6 py-3 font-semibold transition-all duration-200 ${
                                loading ? 'opacity-50 cursor-not-allowed' : ''
                            }`}
                        >
                            {loading ? 'Setting up...' : 'Select Your Players'}
                        </button>
                        <button
                            onClick={() => setOrgStatus(null)}
                            className="w-full text-center text-xs cgm-text-secondary hover:cgm-text-primary transition-colors cursor-pointer"
                        >
                            Connect with a different API key instead
                        </button>
                    </div>
                ) : (
                    <form onSubmit={handleSubmit} className="cgm-card p-6 space-y-4">
                        <div>
                            <label htmlFor="hawkin-api-key" className="block cgm-text-primary text-sm font-medium mb-1">
                                Hawkin API Key
                            </label>
                            <input
                                id="hawkin-api-key"
                                type="password"
                                value={apiKey}
                                onChange={(e) => setApiKey(e.target.value)}
                                placeholder="Paste your Hawkin API key"
                                className="cgm-input w-full"
                                disabled={loading}
                                autoComplete="off"
                            />
                            <p className="cgm-text-secondary text-xs mt-1">
                                Get an API key from cloud.hawkindynamics.com → Integrations
                            </p>
                        </div>
                        <button
                            type="submit"
                            disabled={loading}
                            className={`cgm-button w-full px-6 py-3 font-semibold transition-all duration-200 ${
                                loading ? 'opacity-50 cursor-not-allowed' : ''
                            }`}
                        >
                            {loading ? 'Connecting...' : 'Connect Hawkin'}
                        </button>
                    </form>
                )}
            </div>

            {showMatching && (
                <PlayerMatchingModal
                    provider="hawkin"
                    onNothingToReview={() => {
                        setShowMatching(false);
                        setSuccess('Hawkin connected! All players already linked. Data will sync shortly.');
                    }}
                    onClose={() => {
                        setShowMatching(false);
                        setSuccess('Hawkin connected! Your test data will sync shortly.');
                    }}
                    onComplete={(results) => {
                        setShowMatching(false);
                        const parts = [];
                        if (results.linked > 0) parts.push(`${results.linked} linked`);
                        if (results.created > 0) parts.push(`${results.created} created`);
                        if (results.reinvited > 0) parts.push(`${results.reinvited} re-invited`);
                        if (results.unlinked > 0) parts.push(`${results.unlinked} unlinked`);
                        if (results.skipped > 0) parts.push(`${results.skipped} skipped`);
                        setSuccess(
                            `Hawkin connected! Players: ${parts.join(', ') || 'none processed'}. Data will sync shortly.`
                        );
                    }}
                />
            )}
        </div>
    );
};

export default ConnectHawkin;
